import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit, Trash2, Eye, EyeOff, Settings, Save, X,
  MessageSquare, Briefcase, Calendar, GraduationCap, Trophy,
  Globe, Users, TrendingUp, Star, AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const CategoryManagement = ({ onClose, onCategoryCreated }) => {
  const profile = useSelector(state => state.profile);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);

  const iconOptions = [
    { value: 'MessageSquare', label: 'Message Square', icon: MessageSquare },
    { value: 'Briefcase', label: 'Briefcase', icon: Briefcase },
    { value: 'Calendar', label: 'Calendar', icon: Calendar },
    { value: 'GraduationCap', label: 'Graduation Cap', icon: GraduationCap },
    { value: 'Trophy', label: 'Trophy', icon: Trophy },
    { value: 'Globe', label: 'Globe', icon: Globe },
    { value: 'Users', label: 'Users', icon: Users },
    { value: 'TrendingUp', label: 'Trending Up', icon: TrendingUp },
    { value: 'Star', label: 'Star', icon: Star }
  ];

  const colorOptions = [
    { value: '#0A3A4C', label: 'Primary Blue', color: '#0A3A4C' },
    { value: '#059669', label: 'Green', color: '#059669' },
    { value: '#7C3AED', label: 'Purple', color: '#7C3AED' },
    { value: '#DC2626', label: 'Red', color: '#DC2626' },
    { value: '#F59E0B', label: 'Amber', color: '#F59E0B' },
    { value: '#0891B2', label: 'Cyan', color: '#0891B2' },
    { value: '#DB2777', label: 'Pink', color: '#DB2777' },
    { value: '#65A30D', label: 'Lime', color: '#65A30D' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/forumV3/categories/manage`);
      const data = await response.json();
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (categoryId, currentStatus) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/forumV3/categories/${categoryId}/toggle`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setCategories(prev => prev.map(cat => 
          cat._id === categoryId ? { ...cat, isActive: !currentStatus } : cat
        ));
         toast.dismiss() ;  toast.success(data.message);
        if (onCategoryCreated) onCategoryCreated();
      }
    } catch (error) {
      console.error('Error toggling category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId, postCount) => {
    if (postCount > 0) {
      toast.error('Cannot delete category with existing posts');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/forumV3/categories/${categoryId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setCategories(prev => prev.filter(cat => cat._id !== categoryId));
         toast.dismiss() ;  toast.success('Category deleted successfully');
        // if (onCategoryCreated) onCategoryCreated();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };

  const handleCategoryCreated = (newCategory) => {
    setCategories(prev => [newCategory, ...prev]);
    setShowAddForm(false);
     toast.dismiss() ;  toast.success('Category created successfully');
    if (onCategoryCreated) onCategoryCreated();
  };

  const handleCategoryUpdated = (updatedCategory) => {
    setCategories(prev => prev.map(cat => 
      cat._id === updatedCategory._id ? updatedCategory : cat
    ));
    setEditingCategory(null);
     toast.dismiss() ;  toast.success('Category updated successfully');
    if (onCategoryCreated) onCategoryCreated();
  };

  const getIcon = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : MessageSquare;
  };

  if (!profile.admin) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center"
        >
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600 mb-4">You need admin privileges to manage categories.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 dynamic-site-bg text-white rounded-lg hover:opacity-90 transition-opacity duration-200"
          >
            Close
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 dynamic-site-bg rounded-lg flex items-center justify-center">
              <Settings size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Category Management</h2>
              <p className="text-sm text-gray-600">Manage forum categories and settings</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-2 px-4 py-2 dynamic-site-bg text-white rounded-lg hover:opacity-90 transition-opacity duration-200"
            >
              <Plus size={16} />
              Add Category
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition-colors duration-200"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)] ">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-gray-100 rounded-lg p-4 animate-pulse">
                  <div className="h-6 bg-gray-300 rounded w-1/3 mb-2"></div>
                  <div className="h-4 bg-gray-300 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {categories.map((category) => {
                const IconComponent = getIcon(category.icon);
                return (
                  <motion.div
                    key={category._id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-white border-2 rounded-lg p-4 transition-all duration-200 ${
                      category.isActive ? 'border-gray-200' : 'border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: category.color }}
                        >
                          <IconComponent size={20} className="text-white" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`font-semibold ${
                              category.isActive ? 'text-gray-900' : 'text-gray-500'
                            }`}>
                              {category.name}
                            </h3>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              category.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {category.isActive ? 'Active' : 'Inactive'}
                            </span>
                            <span className="dynamic-site-bg-3 text-white px-2 py-1 rounded-full text-xs font-medium">
                              {category.postCount || 0} posts
                            </span>
                          </div>
                          <p className={`text-sm ${
                            category.isActive ? 'text-gray-600' : 'text-gray-400'
                          }`}>
                            {category.description}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleToggleActive(category._id, category.isActive)}
                          className={`p-2 rounded-lg transition-colors duration-200 ${
                            category.isActive
                              ? 'text-gray-600 hover:dynamic-site-bg-3'
                              : 'text-gray-400 hover:dynamic-site-bg-3'
                          }`}
                          title={category.isActive ? 'Deactivate' : 'Activate'}
                        >
                          {category.isActive ? <Eye size={16} /> : <EyeOff size={16} />}
                        </button>
                        
                        <button
                          onClick={() => setEditingCategory(category)}
                          className="p-2 text-gray-600 hover:dynamic-site-bg-3 rounded-lg transition-colors duration-200"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        
                        <button
                          onClick={() => handleDeleteCategory(category._id, category.postCount)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                          title="Delete"
                          disabled={category.postCount > 0}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}

              {categories.length === 0 && (
                <div className="text-center py-12 bg-white rounded-lg">
                  <MessageSquare size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Categories Found</h3>
                  <p className="text-gray-600">Create your first category to get started.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Add/Edit Category Forms */}
        <AnimatePresence>
          {showAddForm && (
            <CategoryForm
              onClose={() => setShowAddForm(false)}
              onSave={handleCategoryCreated}
              iconOptions={iconOptions}
              colorOptions={colorOptions}
            />
          )}

          {editingCategory && (
            <CategoryForm
              category={editingCategory}
              onClose={() => setEditingCategory(null)}
              onSave={handleCategoryUpdated}
              iconOptions={iconOptions}
              colorOptions={colorOptions}
              isEditing
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Category Form Component
const CategoryForm = ({ category, onClose, onSave, iconOptions, colorOptions, isEditing = false }) => {
  const profile = useSelector(state => state.profile);
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    icon: category?.icon || 'MessageSquare',
    color: category?.color || '#0A3A4C'
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validation
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      const url = isEditing 
        ? `${process.env.REACT_APP_API_URL}/api/forumV3/categories/${category._id}`
        : `${process.env.REACT_APP_API_URL}/api/forumV3/categories`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        onSave(data.category);
      } else {
        setErrors({ general: data.message || 'Failed to save category' });
      }
    } catch (error) {
      console.error('Error saving category:', error);
      setErrors({ general: 'Failed to save category. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const getIcon = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : MessageSquare;
  };

  const PreviewIcon = getIcon(formData.icon);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="absolute inset-0 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? 'Edit Category' : 'Add New Category'}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:dynamic-site-bg-3 flex items-center justify-center transition-colors duration-200"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>

          {errors.general && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-600">{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Category Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dynamic-site-bg focus:border-dynamic-site-bg transition-colors duration-200 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter category name"
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={3}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-dynamic-site-bg focus:border-dynamic-site-bg transition-colors duration-200 resize-none ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter category description"
              />
              {errors.description && (
                <p className="text-sm text-red-600 mt-1">{errors.description}</p>
              )}
            </div>

            {/* Icon Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Icon
              </label>
              <div className="grid grid-cols-3 gap-2">
                {iconOptions.map((option) => {
                  const IconComp = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleInputChange('icon', option.value)}
                      className={`p-3 border rounded-lg flex flex-col items-center gap-1 transition-all duration-200 ${
                        formData.icon === option.value
                          ? 'border-dynamic-site-bg dynamic-site-bg text-white'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <IconComp size={16} />
                      <span className="text-xs">{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Color
              </label>
              <div className="grid grid-cols-4 gap-2">
                {colorOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleInputChange('color', option.value)}
                    className={`p-3 border rounded-lg flex flex-col items-center gap-1 transition-all duration-200 ${
                      formData.color === option.value
                        ? 'border-gray-900 ring-2 ring-gray-300'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div 
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: option.color }}
                    />
                    <span className="text-xs">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Preview */}
            <div className="bg-gray-100 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Preview:</p>
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: formData.color }}
                >
                  <PreviewIcon size={18} className="text-white" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{formData.name || 'Category Name'}</h4>
                  <p className="text-sm text-gray-600">{formData.description || 'Category description'}</p>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:dynamic-site-bg-3 transition-colors duration-200"
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white dynamic-site-bg rounded-lg hover:opacity-90 transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <Save size={16} />
                )}
                {loading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
              </motion.button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default CategoryManagement;
